import type { FileSchema } from '@ametista-ai/api-schema';
import type { UploadUrlParams } from '@ametista-ai/api-schema/http/types';
import * as Sentry from '@sentry/react';
import axios from 'axios';
import { makeAutoObservable } from 'mobx';

export class FileUploadQueueItem {
  file: File;
  uploadParams: UploadUrlParams;
  fileObj?: FileSchema;
  refId?: string;
  status: 'pending' | 'uploading' | 'done' | 'error' = 'pending';
  progress: number = 0;
  errorCode?: string;
  errorMessage?: string;

  constructor(
    file: File,
    uploadParams: UploadUrlParams,
    opts?: { fileObj?: FileSchema; refId?: string }
  ) {
    makeAutoObservable(this);
    this.file = file;
    this.uploadParams = uploadParams;
    this.fileObj = opts?.fileObj;
    this.refId = opts?.refId;
  }

  setProgress(progress: number) {
    this.progress = progress;
  }

  setStatus(status: 'pending' | 'uploading' | 'done' | 'error') {
    this.status = status;
  }

  setError(code: string | undefined, message: string | undefined) {
    this.errorCode = code;
    this.errorMessage = message;
  }

  getErrorI18nKey() {
    if (!this.errorCode) {
      return 'error.network-error-title';
    }

    if (this.errorCode === '401' || this.errorCode === '403') {
      return 'error.authorization-error-title';
    }

    if (this.errorCode === '502') {
      return 'error.bad-gateway-error-title';
    }

    return 'ui.error';
  }

  upload() {
    const formData = new FormData();
    Object.entries(this.uploadParams.fields).forEach(([key, value]) => {
      formData.append(key, value as string);
    });
    formData.append('file', this.file);

    axios
      .request({
        method: 'POST',
        url: this.uploadParams.url,
        data: formData,
        headers: {
          'Content-Type': this.file.type,
        },
        onUploadProgress: (p) => {
          this.setStatus('uploading');
          this.setProgress((p.loaded / (p.total ?? 1)) * 100);
        },
      })
      .then(() => {
        this.setStatus('done');
      })
      .catch(async (error) => {
        const isAxiosError = !!error && typeof error === 'object' && 'isAxiosError' in error;
        const statusCode = isAxiosError
          ? (error?.response?.status as number | undefined)
          : undefined;
        const message = isAxiosError
          ? ((error?.response?.data?.message as string | undefined) ?? error?.message)
          : (error?.message as string | undefined);

        Sentry.captureException(error, {
          extra: {
            uploadUrl: this.uploadParams.url,
            contentType: this.file?.type,
            fileName: this.file?.name,
            fileSize: this.file?.size,
            refId: this.refId,
            statusCode,
          },
        });

        this.setError(statusCode ? String(statusCode) : undefined, message);
        this.setStatus('error');

        if (this.refId) {
          try {
            await fetch(`/api/v1/post_attachments/${this.refId}`, {
              method: 'DELETE',
            });
          } catch {
            // ignore cleanup failure
          }
        }
      });
  }
}

export class FileUploadQueueStore {
  queue: FileUploadQueueItem[] = [];

  constructor() {
    makeAutoObservable(this);
  }

  add(file: File, uploadParams: UploadUrlParams, opts?: { fileObj?: FileSchema; refId?: string }) {
    this.queue.push(new FileUploadQueueItem(file, uploadParams, opts));
  }

  get isEmpty() {
    return this.queue.length === 0;
  }

  processQueue() {
    if (!this.queue.length) {
      return;
    }

    const hasUploading = this.queue.some((q) => q.status === 'uploading');
    if (hasUploading) {
      return;
    }

    const pendingQueueItem = this.queue.find((q) => q.status === 'pending');
    if (pendingQueueItem) {
      pendingQueueItem.upload();
    }
  }

  clear() {
    let queueItem = this.queue.find((q) => q.status === 'done' || q.status === 'error');
    while (queueItem) {
      this.queue.splice(this.queue.indexOf(queueItem), 1);
      queueItem = this.queue.find((q) => q.status === 'done' || q.status === 'error');
    }
  }
}

export const fileUploadQueue = new FileUploadQueueStore();
